import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import {
  apiGetSkiTransfers,
  apiGetSkiTransferById,
  type SkiTransfer,
} from '../../api/skiTransfers/skiTransferApi'
// Inner page imports (commented out as inner page is disabled):
// import { useRef } from 'react'
// import { toast } from 'react-hot-toast'
// import LocationDropdown from '../../components/LocationDropdown'
// import DateTimePicker, {
//   type DateTimeValue,
//   formatDateLabel,
//   formatTimeLabel,
// } from '../../components/DateTimePicker'
// import { bookingService } from '../../api/services/bookingService'
// import type { Vehicle } from '../../types/booking'

type SkiCardProps = {
  title: string
  subtitle: string
  image: string
  time: string
  tags: readonly string[]
  price?: number
  onClick?: () => void
}

function SkiCard({
  title,
  subtitle,
  image,
  time,
  tags,
  price,
  onClick,
}: SkiCardProps) {
  return (
    <article
      onClick={onClick}
      className={`p-4 bg-white border border-[#EBEBEB] rounded-[32px] flex flex-col h-full shadow-[0_4px_16px_rgba(0,0,0,0.03)] transition-all hover:translate-y-[-4px] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] ${
        onClick ? 'cursor-pointer' : ''
      }`}
    >
      <div className="pt-2 pb-4 text-center">
        <h3 className="font-host text-[19px] font-semibold text-black tracking-tight">
          {title}
        </h3>

        {subtitle && (
          <p className="mt-1 font-host text-[13px] font-medium text-slate-500 leading-snug line-clamp-2">
            {subtitle}
          </p>
        )}
      </div>

      <div className="h-[210px] w-full overflow-hidden rounded-[20px] relative">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="pt-4 pb-2 flex flex-col flex-1 text-center items-center justify-between">
        <div>
          <p className="font-host text-[15px] font-semibold text-black">
            From Salzburg: {time}
          </p>
        </div>

        <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-[13px] font-medium text-[#555555]">
          {tags.map((tag, idx) => (
            <span key={idx} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#A5A5A5] flex-shrink-0" />
              <span>{tag}</span>
            </span>
          ))}
        </div>

        {typeof price === 'number' && (
          <div className="mt-4">
            <span className="font-host text-[13px] text-[#666666]">
              Starting from
            </span>
            <div className="font-host text-[18px] font-bold">
              €{price}
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

/*
================================================================================
INNER DETAIL PAGE CODE COMMENTED OUT AS REQUESTED:
Ski transfers now navigate directly to homepage search with prefilled destination.
Uncomment MapMockup below if inner detail page is restored.
================================================================================
function MapMockup({ locationName }: { locationName: string }) {
  const mapSrc = `https://maps.google.com/maps?q=Salzburg%20to%20${encodeURIComponent(
    locationName,
  )}&t=&z=10&ie=UTF8&iwloc=&output=embed`

  return (
    <div className="w-full h-[360px] rounded-[24px] overflow-hidden border border-slate-200 shadow-sm mt-6">
      <iframe
        title={`Route Map to ${locationName}`}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        src={mapSrc}
        allowFullScreen
        loading="lazy"
      />
    </div>
  )
}
*/

export default function SkiTransferListing() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const destinationId = searchParams.get('id')

  const [destinations, setDestinations] = useState<SkiTransfer[]>([])

  useEffect(() => {
    async function loadDestinations() {
      const res = await apiGetSkiTransfers()

      if (res.success && res.data) {
        const activeTransfers = res.data.filter(
          (transfer) => transfer.active === true,
        )

        setDestinations(activeTransfers)
      }
    }

    loadDestinations()
  }, [])

  const handleSelectSkiDestination = (dest: SkiTransfer) => {
    const params = new URLSearchParams({
      serviceType: 'skitransfers',
      serviceId: dest.id,
      serviceTitle: dest.title,
      toTitle: dest.title,
      toAddress: `${dest.title}, Austria`,
    })
    if (dest.base_price) {
      params.set('servicePrice', String(dest.base_price))
    }
    navigate(`/?${params.toString()}#transfers`)
  }

  // If user visits /ski-transfers?id=..., forward to homepage search with prefilled destination
  useEffect(() => {
    if (destinationId) {
      const found = destinations.find((d) => d.id === destinationId)
      if (found) {
        handleSelectSkiDestination(found)
      } else {
        apiGetSkiTransferById(destinationId).then((res) => {
          if (res.data) {
            handleSelectSkiDestination(res.data)
          }
        })
      }
    }
  }, [destinationId, destinations])

  const scrollToSkiTransfers = () => {
    document
      .getElementById('ski-transfer-destinations')
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
  }

  /*
  ==============================================================================
  INNER DETAIL PAGE STATE & HANDLERS (COMMENTED OUT AS REQUESTED):
  Uncomment below if inner detail page is restored.
  ==============================================================================
  const [selectedDestination, setSelectedDestination] =
    useState<SkiTransfer | null>(null)

  const [adultsCount, setAdultsCount] = useState(2)
  const [childrenCount, setChildrenCount] = useState(0)

  const travelersCount = adultsCount + childrenCount

  const vehiclesRef = useRef<HTMLDivElement>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])

  const [departure, setDeparture] = useState<DateTimeValue>({
    date: new Date().toISOString().split('T')[0],
    time: '09:00',
  })

  const [selectedFrom, setSelectedFrom] = useState<{
    title: string
    address: string
  } | null>(null)

  const [pickupLocationError, setPickupLocationError] = useState(false)

  const [isFromOpen, setIsFromOpen] = useState(false)
  const [isDepartureOpen, setIsDepartureOpen] = useState(false)
  const [isBannerDepartureOpen, setIsBannerDepartureOpen] = useState(false)
  const [showTravelersDropdown, setShowTravelersDropdown] = useState(false)
  const [showCars, setShowCars] = useState(false)
  const [activeInfoTab, setActiveInfoTab] = useState<
    'overview' | 'itinerary'
  >('overview')
  useEffect(() => {
    async function loadDestinations() {
      const res = await apiGetSkiTransfers()

      if (res.success && res.data) {
        const activeTransfers = res.data.filter(
          (transfer) => transfer.active === true,
        )

        setDestinations(activeTransfers)
      }
    }

    loadDestinations()
  }, [])

  useEffect(() => {
    async function loadVehicles() {
      const data = await bookingService.fetchVehicles()
      setVehicles(data)
    }

    loadVehicles()
  }, [])

  useEffect(() => {
    const selectedId = destinationId

    if (selectedId) {
      async function loadDetail(id: string) {
        const res = await apiGetSkiTransferById(id)

        if (res.success && res.data && res.data.active === true) {
          setSelectedDestination(res.data)
        } else {
          setSelectedDestination(null)
        }
      }

      loadDetail(selectedId)
    } else {
      setSelectedDestination(null)
    }
  }, [destinationId])

  const setSelectedDestinationId = (id: string | null) => {
    if (id) {
      setSearchParams({ id })
    } else {
      setSearchParams({})
    }
  }

  const scrollToSkiTransfers = () => {
    document
      .getElementById('ski-transfer-destinations')
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
  }

  const validateBooking = () => {
    if (!selectedFrom) {
      setPickupLocationError(true)
      toast.error('Please provide a pickup location.')
      return false
    }

    setPickupLocationError(false)
    return true
  }

  const handleReserveCar = (vehicleId: string) => {
    if (!validateBooking()) return

    const params = new URLSearchParams({
      serviceType: 'skitransfers',
      skipStops: 'true',
      vehicle: vehicleId,
      adults: adultsCount.toString(),
      children: childrenCount.toString(),
      date: departure.date,
      time: departure.time,
    })

    if (selectedFrom) {
      params.set('fromTitle', selectedFrom.title)
      params.set('fromAddress', selectedFrom.address)
    }

    if (selectedDestination) {
      params.set('toTitle', selectedDestination.title)
      params.set('serviceType', 'skitransfers')
      params.set('serviceId', selectedDestination.id)
      params.set('servicePrice', String(calculatedPrice))
      params.set('serviceTitle', selectedDestination.title)

      if (selectedDestination.time) {
        params.set('serviceDuration', selectedDestination.time)
      }
    }

    navigate(`/booking?${params.toString()}`)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''

    const date = new Date(dateString)

    if (isNaN(date.getTime())) return dateString

    const standardWeekdays = [
      'Sun',
      'Mon',
      'Tue',
      'Wed',
      'Thu',
      'Fri',
      'Sat',
    ]

    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]

    return `${standardWeekdays[date.getDay()]} ${date.getDate()} ${
      months[date.getMonth()]
    }, ${date.getFullYear()}`
  }

  let calculatedPrice = selectedDestination?.base_price ?? 480
  let groupText = 'For a group of 1-3 travelers'

  if (travelersCount <= 3) {
    calculatedPrice = selectedDestination?.base_price ?? 480
    groupText = 'For a group of 1-3 travelers'
  } else if (travelersCount <= 6) {
    calculatedPrice = 600
    groupText = 'For a group of 4-6 travelers'
  } else {
    calculatedPrice = 720
    groupText = 'For a group of 7-8 travelers'
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [selectedDestination])

  const tripHighlights = selectedDestination?.highlights || [
    'Private van exclusively for your group',
    'Scenic alpine and lakeside routes',
    'Flexible photo stops along the journey',
    'Professional local driver',
  ]

  const excludedFeatures =
    selectedDestination?.excludedFeatures &&
    selectedDestination.excludedFeatures.length > 0
      ? selectedDestination.excludedFeatures
      : selectedDestination?.excluded_features &&
          selectedDestination.excluded_features.length > 0
        ? selectedDestination.excluded_features
        : [
            'Ski equipment rental',
            'Ski lift passes',
            'Food & drinks',
            'Gratuities / tips',
          ]

  const expectations = [
    {
      title: 'Door-to-Door Convenience',
      desc: "Your journey starts from your preferred location. Whether it's a hotel, airport, train station, or private address, your driver will pick you up and ensure a comfortable start to your trip.",
    },
    {
      title: 'Relax and Enjoy the Journey',
      desc: 'Sit back and enjoy the scenic drive while traveling in a comfortable private vehicle. No crowded tours, no fixed schedules—just a smooth and stress-free experience.',
    },
    {
      title: 'Explore Freely',
      desc: 'Spend your time discovering the destination at your own pace. Visit attractions, enjoy local cuisine, take photos, or simply relax and enjoy the surroundings.',
    },
    {
      title: 'Perfect for any private group',
      desc: "Whether you're traveling solo, as a family with kids, or as a large group, this service is tailored for your comfort and flexibility. It's the ideal option especially if you have limited time or a busy schedule.",
    },
  ]

  const goodToKnowList = selectedDestination?.goodToKnow || [
    'Free cancellation up to 24 hours before departure.',
    'Child seats available upon request.',
    'Additional stops can be arranged.',
    'Tour timing can be customized.',
    'Suitable for families, couples, and groups.',
  ]
  */

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/*
      ==========================================================================
      INNER DETAIL PAGE JSX (COMMENTED OUT AS REQUESTED):
      Ski transfers now navigate directly to homepage search with prefilled destination.
      Uncomment below if inner detail page is restored.
      ==========================================================================
      selectedDestination ? (
        <div className="flex flex-col flex-1">
          <div className="relative z-20 px-4 bg-white border-b border-slate-100">
            <Header />
          </div>

          <main className="max-w-[1280px] w-full mx-auto px-4 sm:px-6 lg:px-[42px] py-8 sm:pb-12 sm:pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                onClick={() => setSelectedDestinationId(null)}
                className="flex items-center gap-1.5 self-start sm:self-auto rounded-full border border-[#E5E5E5] bg-white px-4 py-2 font-host text-sm font-semibold text-[#333333] transition-all hover:bg-slate-50 active:scale-[0.98]"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="text-[#333333]"
                >
                  <path
                    d="M10 13L5 8L10 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Back</span>
              </button>

              <h1 className="font-host text-[22px] sm:text-[28px] font-bold text-[#111111]">
                Ski Transfer from Salzburg to {selectedDestination.title}
              </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <div className="h-[240px] sm:h-[280px] lg:h-[320px] overflow-hidden rounded-[7px] shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <img
                  src={
                    selectedDestination.images?.[0] ||
                    selectedDestination.image ||
                    '/assets/Skytranfers/Skytransfers details/skydetailed1.png'
                  }
                  alt={selectedDestination.title}
                  className="w-full h-full object-cover transition-transform hover:scale-[1.01] duration-300"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 h-[240px] sm:h-[280px] lg:h-[320px]">
                {(selectedDestination.images?.length
                  ? [
                      selectedDestination.images[1] ||
                        selectedDestination.images[0],
                      selectedDestination.images[2] ||
                        selectedDestination.images[0],
                      selectedDestination.images[3] ||
                        selectedDestination.images[0],
                      selectedDestination.images[4] ||
                        selectedDestination.images[0] ||
                        selectedDestination.image,
                    ]
                  : [
                      '/assets/Skytranfers/Skytransfers details/skydetailed2.png',
                      '/assets/Skytranfers/Skytransfers details/skydetailed3.png',
                      '/assets/Skytranfers/Skytransfers details/skydetailed4.png',
                      '/assets/Skytranfers/Skytransfers details/skydetailed5.png',
                    ]
                ).map((imgSrc, idx) => (
                  <div
                    key={idx}
                    className="overflow-hidden rounded-[7px] shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                  >
                    <img
                      src={imgSrc}
                      alt=""
                      className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>

            <h2 className="font-host text-[20px] sm:text-[24px] font-bold text-[#111111] mt-6">
              {selectedDestination.subtitle}
            </h2>

            <div className="flex flex-wrap items-center gap-6 py-5 border-b border-slate-100 text-[14px] font-medium text-[#4A4A4A]">
              <div className="flex items-center gap-2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-brand-orange"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>

                <span className="font-host text-[14px] font-semibold text-[#444444]">
                  Duration : {selectedDestination.time || '1h 30m'}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-brand-orange"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>

                <span className="font-host text-[14px] font-semibold text-[#444444]">
                  Max People: {selectedDestination?.maxPeople ?? '—'}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-brand-orange"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>

                <span className="font-host text-[14px] font-semibold text-[#444444]">
                  Start Location:{' '}
                  {selectedDestination?.startLocation ||
                    selectedDestination?.routeQuery ||
                    'Salzburg'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-10 lg:gap-14 mt-10">
              <div>
                <div className="rounded-[16px] bg-[#ECECC1]/40 border border-[#ECECC1] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#BFD86E] text-white">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>

                    <span className="font-host text-[17px] font-bold text-slate-800 leading-none">
                      Make your booking
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="relative">
                      <button
                        onClick={() =>
                          setIsBannerDepartureOpen(
                            !isBannerDepartureOpen,
                          )
                        }
                        className="flex min-w-[200px] items-center gap-2.5 rounded-[12px] bg-white px-4 py-2.5 shadow-sm transition-opacity hover:opacity-90"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          className="shrink-0 text-black"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>

                        <span className="select-none font-host text-[14px] font-bold text-black">
                          {formatDate(departure.date)}
                        </span>
                      </button>

                      {isBannerDepartureOpen && (
                        <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-[320px]">
                          <DateTimePicker
                            value={departure}
                            allowNow
                            onCancel={() =>
                              setIsBannerDepartureOpen(false)
                            }
                            onConfirm={(next) => {
                              setDeparture(next)
                              setIsBannerDepartureOpen(false)
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        if (!validateBooking()) return

                        setShowCars(true)

                        setTimeout(
                          () =>
                            vehiclesRef.current?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            }),
                          100,
                        )

                        toast(
                          'Please select a vehicle below to proceed.',
                          { icon: '⬇️' },
                        )
                      }}
                      className="rounded-[10px] bg-black px-5 py-2 font-host text-[14px] font-bold text-white hover:bg-slate-900 active:scale-[0.98]"
                    >
                      Check Availability
                    </button>
                  </div>
                </div>

                {showCars && (
                  <div
                    ref={vehiclesRef}
                    className="mt-8 mb-8 p-6 rounded-[24px] bg-[#F9F9F9] border border-[#E5E5E5] shadow-sm"
                  >
                    <h3 className="font-host text-[20px] font-bold text-[#111111] mb-4">
                      Select your vehicle
                    </h3>

                    <div className="flex flex-col gap-3">
                      {(() => {
                        const sortedVehicles = [...vehicles].sort(
                          (a, b) => {
                            const aFits =
                              Number(a.passengers ?? 0) >=
                              travelersCount

                            const bFits =
                              Number(b.passengers ?? 0) >=
                              travelersCount

                            if (aFits && !bFits) return -1
                            if (!aFits && bFits) return 1

                            return (
                              Number(
                                a.displayOrder ??
                                  a.display_order ??
                                  0,
                              ) -
                              Number(
                                b.displayOrder ??
                                  b.display_order ??
                                  0,
                              )
                            )
                          },
                        )

                        const suitableVehiclesCount =
                          sortedVehicles.filter(
                            (vehicle) =>
                              Number(vehicle.passengers ?? 0) >=
                              travelersCount,
                          ).length

                        return (
                          <>
                            {suitableVehiclesCount === 0 && (
                              <div className="rounded-[14px] border border-[#FCD34D] bg-[#FFFBEB] p-4 text-[13px] text-[#92400E]">
                                <p className="font-semibold">
                                  No fleet available for{' '}
                                  {travelersCount} passengers
                                </p>

                                <p className="mt-1 text-[12px] text-[#B45309]">
                                  All available vehicles seat fewer than{' '}
                                  {travelersCount} passengers. Fleets below
                                  are shown faded as they cannot
                                  accommodate your party.
                                </p>
                              </div>
                            )}

                            {sortedVehicles.map((vehicle, index) => {
                              const isEnoughCapacity =
                                Number(vehicle.passengers ?? 0) >=
                                travelersCount

                              return (
                                <div
                                  key={vehicle.id}
                                  className="w-full"
                                >
                                  {index === suitableVehiclesCount &&
                                    suitableVehiclesCount > 0 && (
                                      <div className="my-2.5 flex items-center gap-3 py-1">
                                        <div className="h-px flex-1 bg-[#E0E0E0]" />

                                        <span className="font-poppins text-[10px] font-semibold uppercase tracking-wider text-[#888888] sm:text-[11px]">
                                          Other fleets (
                                          {sortedVehicles.length -
                                            suitableVehiclesCount}{' '}
                                          seat fewer than{' '}
                                          {travelersCount} passengers)
                                        </span>

                                        <div className="h-px flex-1 bg-[#E0E0E0]" />
                                      </div>
                                    )}

                                  <button
                                    type="button"
                                    disabled={!isEnoughCapacity}
                                    aria-disabled={!isEnoughCapacity}
                                    onClick={() => {
                                      if (
                                        !validateBooking() ||
                                        !isEnoughCapacity
                                      ) {
                                        return
                                      }

                                      handleReserveCar(vehicle.id)
                                    }}
                                    className={`group grid w-full shrink-0 grid-cols-[68px_minmax(0,1fr)_72px] items-center gap-1.5 overflow-hidden rounded-[16px] border px-2 py-3 text-left transition-all duration-200 min-[375px]:grid-cols-[72px_minmax(0,1fr)_72px] min-[375px]:gap-2 min-[420px]:grid-cols-[100px_minmax(0,1fr)_88px] min-[420px]:gap-3 min-[420px]:px-3 min-[420px]:py-4 sm:min-h-[120px] sm:grid-cols-[160px_minmax(0,1fr)_116px] sm:px-4 lg:grid-cols-[120px_minmax(0,1fr)_90px] lg:px-3 lg:py-2 ${
                                      !isEnoughCapacity
                                        ? 'cursor-not-allowed border-[#E4E4E4] bg-[#F8F9FA] opacity-45 shadow-none hover:border-[#E4E4E4] hover:shadow-none'
                                        : 'border-[#D9D9D9] bg-white hover:border-[#9BC344] hover:shadow-[0_5px_15px_rgba(155,195,68,0.18)]'
                                    }`}
                                  >
                                    <div className="flex h-[72px] w-full items-center justify-center min-[420px]:h-[90px] lg:h-[70px]">
                                      <img
                                        src={vehicle.image}
                                        alt={vehicle.name}
                                        className={`max-h-[62px] w-full object-contain min-[420px]:max-h-[78px] lg:max-h-[60px] ${
                                          !isEnoughCapacity
                                            ? 'opacity-60'
                                            : ''
                                        }`}
                                      />
                                    </div>

                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <p className="flex items-center gap-1.5 font-host text-[11px] font-medium text-[#222222]">
                                          <span
                                            className={`h-[10px] w-[10px] shrink-0 rounded-full ${
                                              !isEnoughCapacity
                                                ? 'bg-[#B0B0B0]'
                                                : 'bg-[#C7C7C7]'
                                            }`}
                                          />

                                          {vehicle.category}
                                        </p>

                                        {!isEnoughCapacity && (
                                          <span className="rounded-full bg-[#FEE2E2] px-2 py-0.5 font-poppins text-[9px] font-semibold text-[#DC2626]">
                                            Max {vehicle.passengers} seats
                                          </span>
                                        )}
                                      </div>

                                      <h2
                                        className={`mt-1 truncate font-host text-[15px] font-semibold leading-tight sm:text-[18px] ${
                                          !isEnoughCapacity
                                            ? 'text-[#555555]'
                                            : 'text-black'
                                        }`}
                                      >
                                        {vehicle.name}
                                      </h2>

                                      <div className="mt-2 flex items-center gap-3 text-[#5D5D5D]">
                                        <span
                                          className={`flex items-center gap-1 font-host text-[12px] ${
                                            !isEnoughCapacity
                                              ? 'font-semibold text-[#DC2626]'
                                              : ''
                                          }`}
                                        >
                                          <img
                                            src="/assets/images/icon-adult.png"
                                            alt=""
                                            className={`h-[13px] w-[13px] object-contain ${
                                              !isEnoughCapacity
                                                ? 'opacity-90'
                                                : 'opacity-70'
                                            }`}
                                          />

                                          {vehicle.passengers}

                                          {!isEnoughCapacity ? ' max' : ''}
                                        </span>

                                        <span className="flex items-center gap-1 font-host text-[12px]">
                                          <img
                                            src="/assets/images/icon-child.png"
                                            alt=""
                                            className="h-[13px] w-[13px] object-contain opacity-70"
                                          />

                                          {vehicle.luggage}
                                        </span>
                                      </div>
                                    </div>

                                    <div
                                      className={`flex min-h-[64px] flex-col items-center justify-center rounded-[13px] px-2 py-1.5 transition-colors duration-200 min-[420px]:min-h-[80px] ${
                                        !isEnoughCapacity
                                          ? 'bg-[#ECECEC] text-[#777777]'
                                          : 'bg-[#F5FBE7] group-hover:bg-[#EAF6D4]'
                                      }`}
                                    >
                                      <span
                                        className={`font-host text-[18px] font-semibold leading-none sm:text-[20px] ${
                                          !isEnoughCapacity
                                            ? 'text-[#777777]'
                                            : 'text-black'
                                        }`}
                                      >
                                        €
                                        {Math.round(
                                          Number(calculatedPrice) +
                                            Number(vehicle.price),
                                        )}
                                      </span>

                                      <span
                                        className={`mt-1 whitespace-nowrap font-poppins text-[9px] ${
                                          !isEnoughCapacity
                                            ? 'font-semibold text-[#888888]'
                                            : 'font-semibold text-[#365E92]'
                                        }`}
                                      >
                                        {!isEnoughCapacity
                                          ? 'Not enough seats'
                                          : 'Reserve Now ›'}
                                      </span>
                                    </div>
                                  </button>
                                </div>
                              )
                            })}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                )}

                <section className={showCars ? '' : 'mt-10'}>
                  <span className="inline-block rounded-full bg-slate-100 border border-slate-200 px-3.5 py-1 font-host text-[12px] font-bold text-slate-800">
                    Tour Overview
                  </span>

                  <p className="mt-4 font-host text-base leading-[1.65] text-[#4A4A4A] whitespace-pre-line">
                    {selectedDestination.overview ||
                      selectedDestination.shortDescription ||
                      selectedDestination.subtitle}
                  </p>
                </section> 

                <section className={showCars ? '' : 'mt-10'}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveInfoTab('overview')}
                      className={`rounded-full border px-4 py-1.5 font-host text-[12px] font-bold transition-all ${
                        activeInfoTab === 'overview'
                          ? 'border-slate-200 bg-slate-100 text-slate-800'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Tour Overview
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveInfoTab('itinerary')}
                      className={`rounded-full border px-4 py-1.5 font-host text-[12px] font-bold transition-all ${
                        activeInfoTab === 'itinerary'
                          ? 'border-brand-green bg-slate-100 text-slate-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Itinerary
                    </button>
                  </div>

                  {activeInfoTab === 'overview' ? (
                    <p className="mt-4 font-host text-base leading-[1.65] text-[#4A4A4A] whitespace-pre-line">
                      {selectedDestination.overview ||
                        selectedDestination.shortDescription}
                    </p>
                  ) : (
                    <div className="mt-4 rounded-[12px] border border-slate-100 bg-[#F8FAFC] px-5 py-5 sm:px-6">
                      <h3 className="font-host text-[17px] font-bold text-[#111111]">
                        Itinerary
                      </h3>

                      <p className="mt-2 font-host text-[14px] sm:text-[15px] leading-[1.7] text-[#4A4A4A] whitespace-pre-line">
                        {selectedDestination.itinerary ||
                          'No itinerary available.'}
                      </p>
                    </div>
                  )}
                </section>

                <section className="mt-10">
                  <h3 className="font-host text-[18px] font-bold text-[#111111]">
                    Trip Highlights
                  </h3>

                  <ul className="mt-4 space-y-3.5">
                    {tripHighlights.map((hl, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3"
                      >
                        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-brand-orange">
                          {idx === 0 && (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <rect
                                x="1"
                                y="3"
                                width="15"
                                height="13"
                                rx="2"
                                ry="2"
                              />
                              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                              <circle cx="5.5" cy="18.5" r="2.5" />
                              <circle cx="18.5" cy="18.5" r="2.5" />
                            </svg>
                          )}

                          {idx === 1 && (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                            </svg>
                          )}

                          {idx === 2 && (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                              <circle cx="12" cy="13" r="4" />
                            </svg>
                          )}

                          {idx === 3 && (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 8v4l3 3" />
                            </svg>
                          )}
                        </div>

                        <span className="font-host text-[14px] sm:text-[15px] font-semibold text-[#3C3C3C]">
                          {hl}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>

                {excludedFeatures.length > 0 && (
                  <section className="mt-10">
                    <h3 className="font-host text-[18px] font-bold text-[#111111]">
                      Excluded Features
                    </h3>

                    <ul className="mt-4 space-y-3.5">
                      {excludedFeatures.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-3"
                        >
                          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </div>

                          <span className="font-host text-[14px] sm:text-[15px] font-semibold text-[#555555]">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <section className="mt-10">
                  <span className="inline-block rounded-full bg-slate-100 border border-slate-200 px-3.5 py-1 font-host text-[12px] font-bold text-slate-800 mb-4">
                    What to Expect
                  </span>

                  <div className="space-y-6">
                    {expectations.map((exp, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange font-semibold text-[13px]">
                          {idx + 1}
                        </div>

                        <div>
                          <h4 className="font-host text-[15px] sm:text-[16px] font-bold text-[#111111]">
                            {exp.title}
                          </h4>

                          <p className="mt-1.5 font-host text-[13px] sm:text-[14px] leading-[1.5] text-[#555555]">
                            {exp.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mt-10">
                  <span className="inline-block rounded-full bg-slate-100 border border-slate-200 px-3.5 py-1 font-host text-[12px] font-bold text-slate-800 mb-4">
                    Good to know
                  </span>

                  <ul className="space-y-3.5">
                    {goodToKnowList.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3"
                      >
                        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M13.3 4.3L6 11.6L2.7 8.3"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>

                        <span className="font-host text-[14px] font-semibold text-[#3C3C3C]">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="mt-10">
                  <MapMockup
                    locationName={selectedDestination.title}
                  />
                </section>
              </div>

              <div>
                <div className="sticky top-6 flex flex-col gap-6">
                  <div className="p-6 rounded-[28px] border border-[#E5E5E5] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center justify-between">
                      <div className="hidden">
                        <div className="flex items-baseline gap-1">
                          <span className="font-host text-[26px] font-bold text-[#111111] leading-none">
                            From €{selectedDestination.base_price}
                          </span>
                        </div>

                        <div className="mt-1 font-host text-[12px] text-slate-500 font-medium flex items-center gap-1">
                          <span>per vehicle</span>

                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            className="text-slate-400 cursor-pointer"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line
                              x1="12"
                              y1="8"
                              x2="12.01"
                              y2="8"
                            />
                          </svg>
                        </div>
                      </div>

                      <div className="ml-auto flex items-center gap-1.5 rounded-full bg-[#3b629b] px-4 py-2 text-[12px] font-semibold text-white">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <rect
                            x="3"
                            y="11"
                            width="18"
                            height="9"
                            rx="2"
                          />
                          <path d="M5 11V6a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v5" />
                          <circle
                            cx="7"
                            cy="15"
                            r="1.5"
                            fill="currentColor"
                          />
                          <circle
                            cx="17"
                            cy="15"
                            r="1.5"
                            fill="currentColor"
                          />
                        </svg>

                        <span>Private group ride</span>
                      </div>
                    </div>

                    <h4 className="font-host text-[15px] font-semibold text-[#111111] mt-6 mb-3">
                      Pickup Details & Travelers
                    </h4>

                    <div className="flex flex-col gap-3">
                      <div className="relative">
                        <div
                          className={`flex items-center justify-between rounded-[16px] px-4 py-3 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.02)] cursor-pointer transition-all ${
                            pickupLocationError
                              ? 'border border-red-400 bg-red-50/30 ring-2 ring-red-100'
                              : 'border border-[#EAEAEA]'
                          }`}
                          onClick={() => {
                            setIsFromOpen(!isFromOpen)
                            setIsDepartureOpen(false)
                            setShowTravelersDropdown(false)

                            if (pickupLocationError) {
                              setPickupLocationError(false)
                            }
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-host text-[12px] text-slate-500 font-medium">
                              From Location
                            </span>

                            <span className="font-host text-[14px] font-bold text-black">
                              {selectedFrom
                                ? selectedFrom.title
                                : 'Select Pickup Location'}
                            </span>
                          </div>

                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            className={`text-black transition-transform ${
                              isFromOpen ? 'rotate-180' : ''
                            }`}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>

                        {isFromOpen && (
                          <div className="absolute top-[calc(100%+8px)] left-0 w-full z-50">
                            <LocationDropdown
                              onSelect={(place) => {
                                setSelectedFrom({
                                  title: place,
                                  address: `${place}, Austria`,
                                })
                                setPickupLocationError(false)
                                setIsFromOpen(false)
                              }}
                            />
                          </div>
                        )}

                        {pickupLocationError && (
                          <p className="mt-1.5 px-1 font-host text-[12px] font-medium text-red-500">
                            Please select a pickup location.
                          </p>
                        )}
                      </div>

                      <div className="relative">
                        <div
                          className="flex items-center justify-between border border-[#EAEAEA] rounded-[16px] px-4 py-3 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.02)] cursor-pointer"
                          onClick={() => {
                            setIsDepartureOpen(!isDepartureOpen)
                            setIsFromOpen(false)
                            setShowTravelersDropdown(false)
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-host text-[12px] text-slate-500 font-medium">
                              Date & Time
                            </span>

                            <span className="font-host text-[14px] font-bold text-black">
                              {formatDateLabel(departure.date)} at{' '}
                              {formatTimeLabel(departure)}
                            </span>
                          </div>

                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            className={`text-black transition-transform ${
                              isDepartureOpen
                                ? 'rotate-180'
                                : ''
                            }`}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>

                        {isDepartureOpen && (
                          <div className="absolute top-[calc(100%+8px)] left-0 w-full z-50">
                            <DateTimePicker
                              value={departure}
                              allowNow
                              onCancel={() =>
                                setIsDepartureOpen(false)
                              }
                              onConfirm={(next) => {
                                setDeparture(next)
                                setIsDepartureOpen(false)
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div className="relative">
                        <div
                          className="flex items-center justify-between border border-[#EAEAEA] rounded-[16px] px-4 py-3 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.02)] cursor-pointer"
                          onClick={() => {
                            setShowTravelersDropdown(
                              !showTravelersDropdown,
                            )
                            setIsFromOpen(false)
                            setIsDepartureOpen(false)
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-host text-[12px] text-slate-500 font-medium">
                              Travelers
                            </span>

                            <span className="font-host text-[14px] font-bold text-black">
                              {travelersCount} Person
                              {travelersCount > 1 ? 's' : ''}
                            </span>
                          </div>

                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            className={`text-black transition-transform ${
                              showTravelersDropdown
                                ? 'rotate-180'
                                : ''
                            }`}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>

                        {showTravelersDropdown && (
                          <>
                            <div
                              className="fixed inset-0 z-30"
                              onClick={() =>
                                setShowTravelersDropdown(false)
                              }
                            />

                            <div className="absolute right-0 mt-2 w-72 rounded-[16px] bg-white border border-slate-100 shadow-xl p-5 z-40">
                              <div className="flex flex-col gap-6">
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-col">
                                    <span className="font-host text-[15px] font-bold text-black">
                                      Adults
                                    </span>

                                    <span className="font-host text-[12px] text-slate-400">
                                      Age 12+
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setAdultsCount(
                                          Math.max(
                                            1,
                                            adultsCount - 1,
                                          ),
                                        )
                                      }
                                      disabled={adultsCount <= 1}
                                      className="w-9 h-9 flex items-center justify-center rounded-full bg-[#E5E5E5] text-black hover:bg-[#D9D9D9] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                    >
                                      —
                                    </button>

                                    <span className="w-6 text-center font-host text-[15px] font-bold text-black">
                                      {adultsCount}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (
                                          adultsCount +
                                            childrenCount <
                                          8
                                        ) {
                                          setAdultsCount(
                                            adultsCount + 1,
                                          )
                                        }
                                      }}
                                      disabled={
                                        adultsCount +
                                          childrenCount >=
                                        8
                                      }
                                      className="w-9 h-9 flex items-center justify-center rounded-full bg-[#BAD67E] text-black hover:bg-[#b0d26c] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between">
                                  <div className="flex flex-col">
                                    <span className="font-host text-[15px] font-bold text-black">
                                      Children
                                    </span>

                                    <span className="font-host text-[12px] text-slate-400">
                                      Age 0-12
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setChildrenCount(
                                          Math.max(
                                            0,
                                            childrenCount - 1,
                                          ),
                                        )
                                      }
                                      disabled={childrenCount <= 0}
                                      className="w-9 h-9 flex items-center justify-center rounded-full bg-[#E5E5E5] text-black hover:bg-[#D9D9D9] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                    >
                                      —
                                    </button>

                                    <span className="w-6 text-center font-host text-[15px] font-bold text-black">
                                      {childrenCount}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (
                                          adultsCount +
                                            childrenCount <
                                          8
                                        ) {
                                          setChildrenCount(
                                            childrenCount + 1,
                                          )
                                        }
                                      }}
                                      disabled={
                                        adultsCount +
                                          childrenCount >=
                                        8
                                      }
                                      className="w-9 h-9 flex items-center justify-center rounded-full bg-[#BAD67E] text-black hover:bg-[#b0d26c] disabled:opacity-40 disabled:cursor-not-allowed select-none font-bold text-lg transition-colors"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex gap-2.5 items-start border border-[#CCE5CC] rounded-[12px] bg-[#F7FDF7] p-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="text-green-700 flex-shrink-0 mt-0.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>

                      <span className="font-host text-[13px] font-semibold text-green-800 leading-normal">
                        Free cancellation up to 24 hours before your
                        pickup time.
                      </span>
                    </div>

                    <div className="hidden mt-4 p-4 rounded-[16px] bg-[#F0F2F5] flex-col">
                      <span className="font-host text-[13px] font-medium text-slate-500">
                        {groupText}
                      </span>

                      <div className="flex items-center justify-between mt-1">
                        <span className="font-host text-[18px] font-bold text-black">
                          Total
                        </span>

                        <span className="font-host text-[24px] font-bold text-black">
                          €{calculatedPrice}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        if (!validateBooking()) return
                        setShowCars(true)
                      }}
                      className="w-full mt-4 rounded-full bg-[#FF6A1A] hover:bg-[#E05B13] active:scale-[0.98] py-4 font-host text-[16px] font-bold text-white shadow-[0_4px_12px_rgba(255,106,26,0.15)] transition-all"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      ) : null
      */}

      <div className="flex flex-col flex-1 bg-white">
        <section className="relative w-full bg-white md:hidden">
            <div className="relative z-20 w-full px-4">
              <Header />
            </div>

            <div className="relative mx-[10px] h-[390px] overflow-hidden">
              <img
                src="/assets/Skytranfers/skitransferhero.png"
                alt="Ski Tours"
                className="absolute inset-0 h-full w-full object-cover object-top"
              />

              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[145px] bg-gradient-to-t from-white via-white/80 to-transparent" />

              <div className="absolute inset-x-0 bottom-[10px] z-10 flex flex-col items-center px-6 text-center">
                <span className="font-host text-[14px] font-medium tracking-[0.02em] text-[#666666]">
                  [ Ski Tours ]
                </span>

                <p className="mt-[10px] max-w-[250px] font-host text-[14px] font-normal leading-[1.35] text-[#666666]">
                  Private ski tours with easy pickup and drop-off.
                </p>
              </div>
            </div>

            <div className="px-[18px] pt-[8px] text-center">
              <h1 className="mx-auto max-w-[350px] font-host text-[24px] font-bold leading-[1.18] tracking-[-0.035em] text-[#111111]">
                Discover Austria&apos;s Best Winter
                <br />
                Destinations
              </h1>

              <p className="mx-auto mt-[24px] max-w-[335px] font-host text-[14px] font-normal leading-[1.35] text-[#666666]">
                Enjoy a seamless winter journey with professional
                <br />
                drivers, modern vehicles, and door-to-door
                <br />
                service.
              </p>

              <a
                href="#ski-transfer-destinations"
                onClick={(event) => {
                  event.preventDefault()
                  scrollToSkiTransfers()
                }}
                className="mx-auto mt-[14px] flex h-[50px] min-w-[148px] items-center justify-center gap-[7px] rounded-[18px] bg-black px-[24px] font-host text-[13px] font-medium text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>EXPLORE NOW</span>

                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 12H19"
                    stroke="#B6D438"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />

                  <path
                    d="M14 7L19 12L14 17"
                    stroke="#B6D438"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </section>

          <div className="hidden md:block">
            <section className="relative overflow-hidden h-[520px] sm:h-[650px] lg:h-[960px] bg-white">
              <img
                src="/assets/Skytranfers/skitransferhero.png"
                alt=""
                className="absolute left-1/2 top-[190px] z-0 h-auto w-[89%] -translate-x-1/2 object-contain"
              />

              <div className="absolute top-0 left-0 right-0 z-20 px-4">
                <Header />
              </div>

              <div className="relative z-10 mx-auto max-w-page w-full px-4 sm:px-6 lg:px-[42px] flex flex-col lg:flex-row lg:justify-between lg:items-start h-full pt-28 pb-10">
                <div className="max-w-[280px] lg:max-w-[320px] mt-8 lg:mt-[270px] bg-white/20 backdrop-blur-xs px-4 py-3 rounded-[12px] border border-white/20">
                  <p className="font-host text-[15px] sm:text-[17px] font-medium leading-[1.5] text-slate-900">
                    Private ski tours with easy pickup and drop-off.
                  </p>
                </div>

                <div className="max-w-[480px] mt-8 lg:mt-12 lg:text-right flex flex-col lg:items-end">
                  <span className="font-host text-[14px] font-bold text-slate-600 uppercase tracking-widest bg-white/30 px-3 py-1 rounded-full">
                    [ Ski Tours ]
                  </span>

                  <h1 className="mt-4 font-host text-[28px] sm:text-[36px] lg:text-[42px] font-bold leading-[1.25] text-slate-900 tracking-[-0.03em]">
                    Discover Austria&apos;s Best{' '}
                    <br className="hidden sm:block" /> Winter Destinations
                  </h1>

                  <p className="mt-4 font-host text-[14px] sm:text-[16px] leading-[1.5] text-slate-700 font-medium">
                    Enjoy a seamless winter journey with professional
                    drivers,{' '}
                    <br className="hidden lg:block" /> modern vehicles,
                    and door-to-door service.
                  </p>

                  <a
                    href="#ski-transfer-destinations"
                    onClick={(event) => {
                      event.preventDefault()
                      scrollToSkiTransfers()
                    }}
                    className="mt-6 flex items-center gap-2 rounded-[24px] bg-black px-6 py-3 font-host text-[14px] font-bold text-white shadow-md transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <span>EXPLORE NOW</span>
                    <span className="text-[16px]">→</span>
                  </a>
                </div>
              </div>
            </section>
          </div>

          <main
            id="ski-transfer-destinations"
            className="scroll-mt-6 flex-1 max-w-[1280px] w-full mx-auto px-4 sm:px-6 lg:px-[42px] pb-16 pt-0"
          >
            <h2 className="pt-[38px] text-center font-host text-[40px] font-bold leading-[1.12] tracking-[-0.04em] text-[#D7D7D7] md:hidden">
              Popular Ski
              <br />
              Destinations
            </h2>

            <h2 className="hidden text-center font-host text-[26px] sm:text-[34px] font-bold leading-[1.25] tracking-[-0.02em] text-[#333333]/30 uppercase md:block">
              Popular Ski Destinations
            </h2>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mt-12">
              {destinations.map((dest) => (
                <SkiCard
                  key={dest.id}
                  title={dest.title}
                  subtitle={dest.subtitle}
                  image={dest.image}
                  time={dest.time}
                  tags={dest.tags}
                  onClick={() => handleSelectSkiDestination(dest)}
                />
              ))}
            </div>
          </main>
        </div>

      <Footer />
    </div>
  )
}